import { ActivationPayload, CrcDecision, CrcFormPayload, DashboardNotifications, ProductCode } from '@dev/ee-api/ee-api-models';
import { BillingPaths, ContactsPaths, HomePaths, LegacyPaths, SettingsPaths, UpsellingPaths } from '@api/services/dictionaries/paths';
import {
    CrcValidate,
    GetAccountWarnings,
    GetAccountsNameStart,
    LoginWithDisposableToken,
    PurchasePrivateIP,
    SendCrcForm,
    UpdateCRCFormParam,
} from '@api/store/account/actions';
import { HotJarEvents, useHotJar } from '@api/services/hooks/useHotjar';
import { LoadPaymentProviders, getProviders } from '@api/store/payments/actions';
import React, { FC, useEffect } from 'react';
import { UpdateDashboardSeenNotification, getDashboardSeenNotificationsStore } from '@api/store/dashboardSeenNotifications/actions';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';

import { ChangeProduct } from '@api/store/products/actions';
import { ChangeProductDialog } from '../ChangeProductDialog';
import { DeletePurchaseInfo } from '@api/store/pools/actions';
import { DiscountBanner } from '@api/services/Layout/DiscountBanner';
import { GoogleAnalyticsEventsEnum } from '@api/services/dictionaries/GoogleAnalyticsEvents';
import { Home as HomeModule } from '@dev/ee-modules';
import { HomeProps } from './HomeInterface';
import { LoadDomains } from '@api/store/domain/actions';
import { LoadInboundRoutes } from '@api/store/inboundRoutes/actions';
import { LoadWebhooks } from '@api/store/webhooks/actions';
import { LoginWithDisposableTokenPayload } from '@api/store/account/types';
import PLACEHOLDER_IMAGE from '../../images/placeholder.svg';
import ReactivationForm from './CRC';
import { ResendActivationEmail } from '@api/store/auth/actions';
import { ShowDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import { ShowToast } from '@api/store/toast/actions';
import { ShowToastParams } from '@api/store/toast/types';
import { ApplicationState as State } from '@api/store';
import { StripePolicyChangeAlert } from '@api/services/Layout/Alerts/StripePolicyChangeAlert';
import { TemplatesPaths } from '@api/dashboards/Marketing/paths';
import { Theme } from '@dev/ee-components';
import { compose } from 'react-recompose';
import { getConfigStore } from '@api/store/config/types';
import { DPAUpdatedBanner } from '@api/services/Layout/DPABanner';
import { getNotificationStore } from '@api/store/notifications/actions';
import { isIntercomEnabled } from '@api/services/Security';
import { isNil } from 'lodash-es';
import { links } from '@api/services/dictionaries/links';
import { push } from 'redux-first-history';
import styled from 'styled-components';
import { useAvailableSwitchProduct } from '@api/services/hooks/useAvailableSwitchProduct';
import { useCurrentPlanStatus } from '@api/services/hooks/useCurrentPlanStatus';
import { useDiscount } from '@api/services/hooks/useDiscount';
import { useIntercom } from 'react-use-intercom';
import { useNotificationCenter } from '@api/services/hooks/useNotificationCenter';
import { findPersonaSurveyNotificationName } from '@api/services/Dialog/personaSurveyDialog/tools/findPersonaSurveyNotificationName';

type StyledProps = {
    adjustPaddingForBanner?: boolean;
};

const HomeWrapper = styled.div<StyledProps>`
    ${(props) =>
        props?.adjustPaddingForBanner &&
        `
        .MuiContainer-root{
            ${Theme.breakpoints.up('sm')} {
                padding-top:64px;
        }
    `}
`;

const mapState = (state: State, ownProps: HomeProps): HomeProps => {
    return {
        ...ownProps,
        account: state.account,
        domain: state.domain,
        browser: state.browser,
        webhooks: state.webhooks,
        inbound: state.inbound,
        config: state.config,
        products: state.products,
        payments: state.payments,
    };
};

const dispatchProps = {
    LoadDomains,
    GetAccountWarnings,
    Push: push,
    UpdateCRCFormParam,
    SendCrcForm,
    CrcValidate,
    GetAccountsNameStart,
    LoadWebhooks,
    LoadInboundRoutes,
    ShowDialog,
    DeletePurchaseInfo,
    PurchasePrivateIP,
    LoginWithDisposableToken,
    ShowToast,
    ResendActivationEmail,
};

interface DispatchProps {
    LoadDomains(): void;
    Push(url: string): void;
    GetAccountWarnings(): void;
    UpdateCRCFormParam(params: CrcFormPayload): void;
    SendCrcForm(): void;
    CrcValidate(decision: CrcDecision): void;
    GetAccountsNameStart(): void;
    LoadWebhooks(): void;
    LoadInboundRoutes(): void;
    ShowDialog(params: ShowDialogParams): void;
    DeletePurchaseInfo(): void;
    PurchasePrivateIP(params: Record<string, any>): void;
    LoginWithDisposableToken(params: LoginWithDisposableTokenPayload): void;
    ResendActivationEmail(payload: ActivationPayload): void;
    ShowToast(params: ShowToastParams): void;
}

type ExtendedProps = HomeProps & DispatchProps;

const domainAuthenticationRequirementsTargetDate = new Date('2024-01-29 14:30:00+01').getTime();

const Home: FC<ExtendedProps> = (props: ExtendedProps): JSX.Element => {
    const { boot, show } = useIntercom() || {};
    const dispatch = useDispatch();
    const { isPilotPlan, isActive } = useCurrentPlanStatus();
    const providers = useSelector(getProviders);
    const { discountEnabled } = useDiscount();
    const notifications = useSelector(getNotificationStore);
    const { showNotificationDialog, showNotificationBanner } = useNotificationCenter();
    const { t } = useTranslation();
    const { bootedAsChild, isStandalone } = useSelector(getConfigStore);
    const { billingPlanID, defaultTierID, canSeeChangeProduct } = useAvailableSwitchProduct() || {};
    const { list: dashboardSeenNotifications, isLoaded: dashboardSeenNotificationsLoaded } =
        useSelector(getDashboardSeenNotificationsStore);

    const { showSurvey } = useHotJar();
    useEffect(() => {
        isIntercomEnabled() && boot();
        if (!providers) dispatch(LoadPaymentProviders());
    }, []);

    useEffect(() => {
        if (isNil(isActive)) return;
        showSurvey(HotJarEvents.PaidUsersAPI);
        showSurvey(HotJarEvents.FreeUsersAPI);
        showSurvey(HotJarEvents.FreeUsersAPIZeroSent);
        showSurvey(HotJarEvents.NPSEmailAPI);
    }, [isActive]);

    //TLS modal
    useEffect(() => {
        if (!notifications?.notificationList) return;

        const personaSurveryNotification = findPersonaSurveyNotificationName(notifications.notificationList);
        if (personaSurveryNotification) {
            showNotificationDialog(personaSurveryNotification);
        }
        // showNotificationDialog(DashboardNotifications.TLSDicontinueNotification);
    }, [notifications?.notificationList?.length, dashboardSeenNotifications?.length]);

    const launchIntercom = () => {
        isIntercomEnabled() && show();
    };

    const buyIpLegacy = () => {
        if (bootedAsChild) {
            window.location.replace(`${location.origin}${LegacyPaths.BuyLegacyIp}`);
            return;
        }
        props.Push(LegacyPaths.BuyLegacyIp);
    };

    // additional GA eventLabel parameter to remove after Switch Product A/B tests

    const openSwitchProductModal = (): void => {
        dispatch(
            ShowDialog({
                body: <ChangeProductDialog />,
                title: t('changeproduct'),
                confirmLabel: t('changeproduct'),
                cancelLabel: t('cancel'),
                fullScreen: false,
                onClickConfirm: (): void => {
                    dispatch(ChangeProduct(billingPlanID, defaultTierID, GoogleAnalyticsEventsEnum.ApiToMarketingStart));
                },
                isHiddenManually: true,
            })
        );
    };
    const closeAuthenticationRequirementsModal = (): void => {
        dispatch(UpdateDashboardSeenNotification(DashboardNotifications.DMARCNotification));
    };
    const goToDomainsAuthenticationRequirementsModal = (): void => {
        dispatch(UpdateDashboardSeenNotification(DashboardNotifications.DMARCNotification));
        dispatch(push(SettingsPaths.Domains));
    };
    const domainAuthenticationRequirementsIsOpen =
        dashboardSeenNotificationsLoaded && !dashboardSeenNotifications?.includes(DashboardNotifications.DMARCNotification);

    const showStripePolicyAlert = showNotificationBanner(DashboardNotifications.StripePolicyChange);

    return (
        <HomeWrapper adjustPaddingForBanner={discountEnabled}>
            <HomeModule
                {...(props as any)}
                links={links as any}
                paths={{ ...SettingsPaths, ...ContactsPaths, ...HomePaths, ...TemplatesPaths, ...BillingPaths, ...UpsellingPaths }}
                crcForm={ReactivationForm}
                launchIntercom={launchIntercom}
                welcomeDialog={{
                    image: <PLACEHOLDER_IMAGE />,
                }}
                topBanner={!isPilotPlan && <DiscountBanner />}
                productCode={ProductCode.EmailApi}
                BuyIp={(isStandalone || bootedAsChild) && buyIpLegacy}
                onChangeProductButtonClick={openSwitchProductModal}
                showChangeProduct={canSeeChangeProduct}
                dialogsSettings={{
                    domainAuthenticationRequirements: {
                        targetTimestamp: domainAuthenticationRequirementsTargetDate,
                        open: domainAuthenticationRequirementsIsOpen,
                        closeCallback: closeAuthenticationRequirementsModal,
                        confirmCallback: goToDomainsAuthenticationRequirementsModal,
                    },
                }}
                customNotifications={
                    <>
                        {showStripePolicyAlert && (
                            <AlertWithCenteredMessage>
                                <StripePolicyChangeAlert />
                            </AlertWithCenteredMessage>
                        )}
                        <DPAUpdatedBanner />
                    </>
                }
            />
        </HomeWrapper>
    );
};

export default compose(withTranslation(), connect<HomeProps, DispatchProps>(mapState, dispatchProps))(Home);

const AlertWithCenteredMessage = styled.div`
    .MuiAlert-action {
        align-self: center;
    }
`;
